import { useQueryClient } from '@tanstack/react-query';
import _omit from 'lodash/omit';
import _pickBy from 'lodash/pickBy';
import React from 'react';

import type { CsrfData } from 'types/client/account';

import config from 'configs/app';
import { getEnvValue } from 'configs/app/utils';
import isBodyAllowed from 'lib/api/isBodyAllowed';
import isNeedProxy from 'lib/api/isNeedProxy';
import { getResourceKey } from 'lib/api/useApiQuery';
import * as cookies from 'lib/cookies';
import type { Params as FetchParams } from 'lib/hooks/useFetch';
import useFetch from 'lib/hooks/useFetch';

import buildUrl from './buildUrl';
import { RESOURCES } from './resources';
import type { ApiResource, ResourceName, ResourcePathParams } from './resources';

export interface Params<R extends ResourceName> {
  pathParams?: ResourcePathParams<R>;
  queryParams?: Record<string, string | Array<string> | number | boolean | undefined | null>;
  fetchParams?: Pick<FetchParams, 'body' | 'method' | 'signal' | 'headers'>;
  logError?: boolean;
}

export default function useApiFetch() {
  const fetch = useFetch();
  const queryClient = useQueryClient();
  const { token: csrfToken } = queryClient.getQueryData<CsrfData>(getResourceKey('csrf')) || {};

  return React.useCallback(<R extends ResourceName, SuccessType = unknown, ErrorType = unknown>(
    resourceName: R,
    { pathParams, queryParams, fetchParams, logError }: Params<R> = {},
  ) => {
    const apiToken = cookies.get(cookies.NAMES.API_TOKEN);

    const resource: ApiResource = RESOURCES[resourceName];
    const url = buildUrl(resourceName, pathParams, queryParams);
    const withBody = isBodyAllowed(fetchParams?.method);
    const headers = _pickBy({
      'x-endpoint': resource.endpoint && isNeedProxy() ? resource.endpoint : undefined,
      Authorization: resource.endpoint && resource.needAuth ? apiToken : undefined,
      'x-csrf-token': withBody && csrfToken ? csrfToken : undefined,
      ...resource.headers,
      ...fetchParams?.headers,
    }, Boolean) as HeadersInit;

    const isApiLogEnabled = getEnvValue('NEXT_PUBLIC_API_LOG_ENABLED') === 'true';
    const method = fetchParams?.method || 'GET';

    return fetch<SuccessType, ErrorType>(
      url,
      {
        // as of today, we use cookies only
        //    for user authentication in My account
        //    for API rate-limits (cannot use in the condition though, but we agreed with devops team that should not be an issue)
        // change condition here if something is changed
        credentials: config.features.account.isEnabled ? 'include' : 'same-origin',
        headers,
        ..._omit(fetchParams, 'headers'),
      },
      {
        resource: resource.path,
        logError,
      },
    ).then((res) => {
      if (isApiLogEnabled) {
        const methodColors: Record<string, string> = { GET: '#14DCAC', POST: '#3B82F6' };
        const methodColor = methodColors[method] || '#F59E0B';
        // eslint-disable-next-line no-console
        console.groupCollapsed(
          `%c API %c${ method }%c ${ url }`,
          'color: #888; font-weight: bold',
          `color: #fff; background: ${ methodColor }; padding: 2px 6px; border-radius: 3px; font-weight: bold`,
          'color: #14DCAC; font-weight: normal',
        );
        // eslint-disable-next-line no-console
        console.log('%c请求地址:%c ' + url, 'color: #14DCAC; font-weight: bold', 'color: #fff');
        if (queryParams && Object.keys(queryParams).length > 0) {
          // eslint-disable-next-line no-console
          console.log('%c请求参数:', 'color: #14DCAC; font-weight: bold', queryParams);
        }
        if (fetchParams?.body) {
          // eslint-disable-next-line no-console
          console.log('%c请求体:', 'color: #14DCAC; font-weight: bold', fetchParams.body);
        }
        // eslint-disable-next-line no-console
        console.log('%c响应数据:', 'color: #14DCAC; font-weight: bold', res);
        // eslint-disable-next-line no-console
        console.groupEnd();
      }
      return res;
    });
  }, [ fetch, csrfToken ]);
}
